/* eslint-disable react-refresh/only-export-components */
import { useMemo, useState, useEffect } from 'react';
import {
  Button,
  CustomSelect,
  Modal,
  SearchInput,
  SkeletonBox,
  SkeletonText,
} from '@/components';
import { useQuotationPdfTemplates } from '../hooks';
import {
  DEFAULT_PDF_TEMPLATE_KEY,
  FIELD_LABEL_CLASS,
  resolveQuotationMediaUrl,
  preloadPdfPreviews,
} from '../utils';
import {
  TemplatePdfThumbnail,
  TemplatePdfViewer,
} from './TemplatePdfThumbnail';

function TemplateDetailModal({
  template,
  companyId,
  isOpen,
  onClose,
  onSelect,
  isSelected,
}) {
  const thumb = resolveQuotationMediaUrl(template?.preview_image_url);

  if (!template) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2.5 flex-wrap">
          <span>{template.name}</span>
          {template.is_popular && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary text-white shadow-xs tracking-normal">
              ★ Most Popular
            </span>
          )}
        </span>
      }
      description={
        template.category
          ? `${template.category} PDF Quotation Template`
          : 'PDF Quotation Template'
      }
      size="2xl"
      zIndexClass="z-[110]"
    >
      <div className="flex flex-col gap-5">
        <div className="relative w-full rounded-xl overflow-hidden border border-border-default min-h-[320px] h-[60vh]">
          <TemplatePdfViewer
            companyId={companyId}
            templateKey={template.key}
            templateName={template.name}
            enabled={isOpen}
            onErrorFallback={
              thumb ? (
                <img
                  src={thumb}
                  alt=""
                  className="mt-2 max-h-40 w-auto object-contain rounded-md border border-border-default opacity-80"
                />
              ) : null
            }
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-border-default">
          <Button type="button" variant="secondary" onClick={onClose}>
            Back to Templates
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              onSelect(template.key);
              onClose();
            }}
          >
            {isSelected ? 'Keep This Template' : 'Use This Template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplateCard({
  template,
  selected,
  disabled,
  onSelect,
  onPreview,
  companyId,
  priority = 5,
  showDescription = true,
}) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={() => !disabled && onSelect(template.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!disabled) onSelect(template.key);
        }
      }}
      className={`group relative flex flex-col text-left rounded-xl border bg-white overflow-hidden transition-all outline-hidden cursor-pointer ${
        selected
          ? 'border-primary ring-2 ring-primary/25 shadow-md'
          : 'border-border-default hover:border-primary/50 hover:shadow-sm hover:-translate-y-0.5'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <div className="relative aspect-[16/10] bg-[#F5F5F5] overflow-hidden">
        <TemplatePdfThumbnail
          companyId={companyId}
          template={template}
          priority={priority}
        />

        {template.is_popular && (
          <span className="absolute top-2 left-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-primary text-white shadow-sm z-[1]">
            Popular
          </span>
        )}

        {selected && (
          <span className="absolute top-2 right-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-md z-[1]">
            <svg
              className="w-3 h-3 sm:w-3.5 sm:h-3.5"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="2 6 5 9 10 3" />
            </svg>
          </span>
        )}

        <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 p-3 backdrop-blur-[1px] z-[1]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreview?.(template);
            }}
            className="px-3 py-1.5 rounded-lg bg-white/95 hover:bg-white text-text-primary text-xs font-medium shadow-md transition-transform active:scale-95 flex items-center gap-1.5"
          >
            <svg
              className="w-3.5 h-3.5 text-text-secondary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>Preview</span>
          </button>
        </div>
      </div>

      <div className="p-2.5 sm:p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-text-primary leading-snug truncate">
            {template.name}
          </p>
          {template.category && (
            <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-[#F0F7F0] text-primary">
              {template.category}
            </span>
          )}
        </div>
        {showDescription && template.description && (
          <p className="mt-1 text-[11px] text-text-muted leading-snug line-clamp-2">
            {template.description}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Dynamic PDF design picker — same card UI as Select Quotation Template modal.
 * compact=true → sidebar-friendly 2-col grid + filters.
 */
export function PdfTemplatePicker({
  companyId,
  value,
  onChange,
  disabled = false,
  label = 'PDF Design',
  helperText = 'Optional. Controls how the quotation PDF looks. Defaults to Modern Corporate.',
  compact = false,
  showSearch = true,
  showCategoryFilter = true,
  showDescription = true,
  gridClassName,
}) {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [previewingTemplate, setPreviewingTemplate] = useState(null);

  const { data, isLoading, isError, refetch } =
    useQuotationPdfTemplates(companyId);

  const templates = useMemo(() => {
    const list = Array.isArray(data) ? data : data?.results || [];
    return list;
  }, [data]);

  const selectedKey = value || DEFAULT_PDF_TEMPLATE_KEY;
  const selected = templates.find((t) => t.key === selectedKey);

  useEffect(() => {
    if (companyId && templates.length > 0) {
      preloadPdfPreviews(companyId, templates, selectedKey);
    }
  }, [companyId, templates, selectedKey]);

  const categories = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => {
      if (t.category) set.add(t.category);
    });
    return ['All', ...Array.from(set).sort()];
  }, [templates]);

  const categoryOptions = useMemo(() => {
    return categories.map((cat) => {
      const count =
        cat === 'All'
          ? templates.length
          : templates.filter((t) => t.category === cat).length;
      return { value: cat, label: `${cat} (${count})` };
    });
  }, [categories, templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesCat =
        selectedCategory === 'All' ||
        t.category?.toLowerCase() === selectedCategory.toLowerCase();
      if (!matchesCat) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase().trim();
      return (
        t.name?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q) ||
        t.key?.toLowerCase().includes(q)
      );
    });
  }, [templates, selectedCategory, search]);

  const resolvedGridClass =
    gridClassName ||
    (compact
      ? 'grid-cols-3'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4');

  const showFilters = showSearch || showCategoryFilter;

  return (
    <div className="flex flex-col gap-2.5 min-h-0">
      <div className="flex items-end justify-between gap-2 shrink-0">
        <div className="min-w-0">
          {label && <p className={FIELD_LABEL_CLASS}>{label}</p>}
          {helperText && (
            <p className="text-[11px] text-text-muted leading-snug mt-0.5">
              {helperText}
            </p>
          )}
        </div>
        {selected?.name && (
          <span className="shrink-0 text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-md">
            {selected.name}
          </span>
        )}
      </div>

      {showFilters && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
          {showSearch && (
            <div className="flex-1 min-w-0">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates…"
                id="pdf-template-search"
                className="w-full"
                inputClassName="!h-9 !rounded-xl text-xs"
              />
            </div>
          )}
          {showCategoryFilter && categories.length > 1 && (
            <div className={compact ? 'w-full sm:w-40' : 'w-full sm:w-52'}>
              <CustomSelect
                value={selectedCategory}
                onChange={(val) => setSelectedCategory(val || 'All')}
                options={categoryOptions}
                searchable
                placeholder="All"
                textSizeClass="text-xs"
                className="w-full h-9"
                buttonClassName="w-full h-9 flex items-center justify-between rounded-xl bg-white border border-border-default hover:border-primary/50 text-text-primary text-xs font-medium focus:outline-none focus:border-primary px-3 transition-all cursor-pointer shadow-2xs"
                dropdownWidth={200}
                hideCheckIcon={false}
              />
            </div>
          )}
          <div className="text-[11px] text-text-muted shrink-0 font-medium self-center whitespace-nowrap">
            {filteredTemplates.length}{' '}
            {filteredTemplates.length === 1 ? 'template' : 'templates'}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className={`grid gap-2.5 ${resolvedGridClass}`}>
          {Array.from({ length: compact ? 4 : 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-border-default overflow-hidden bg-white"
            >
              <SkeletonBox className="aspect-[16/10] w-full rounded-none" />
              <div className="p-3 space-y-2">
                <SkeletonText width="w-28" height="h-3.5" />
                <SkeletonText width="w-full" height="h-3" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-center">
          <p className="text-[11px] text-amber-800 leading-snug">
            Could not load PDF designs. Saving without a selection still works —
            backend uses the default template.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => refetch()}
            className="mt-2 text-xs h-8 px-3"
          >
            Retry
          </Button>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border-default px-3 py-6 text-center text-xs text-text-muted">
          No templates match your filters.
          {(search || selectedCategory !== 'All') && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSelectedCategory('All');
              }}
              className="mt-2 block mx-auto text-primary font-medium hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className={`grid gap-2.5 ${resolvedGridClass}`}>
          {filteredTemplates.map((tpl, idx) => (
            <TemplateCard
              key={tpl.key}
              template={tpl}
              selected={tpl.key === selectedKey}
              disabled={disabled}
              onSelect={onChange}
              onPreview={setPreviewingTemplate}
              companyId={companyId}
              priority={idx < 4 ? 1 : 5}
              showDescription={showDescription}
            />
          ))}
        </div>
      )}

      <TemplateDetailModal
        template={previewingTemplate}
        companyId={companyId}
        isOpen={!!previewingTemplate}
        onClose={() => setPreviewingTemplate(null)}
        onSelect={onChange}
        isSelected={previewingTemplate?.key === selectedKey}
      />
    </div>
  );
}

/** Resolve display name from a loaded template list (or fall back to key). */
export function pdfTemplateLabel(templates, key) {
  if (!key) return 'Default';
  const list = Array.isArray(templates) ? templates : [];
  const match = list.find((t) => t.key === key);
  return match?.name || key;
}
